import {DnDDividerPlacementType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDDividerPlacementType';
import {DnDDropTargetHighlightType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDDropTargetHighlightType';
import {DnDHoveredDropTargetHighlightType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDHoveredDropTargetHighlightType';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {Background} from '@mintdata/mint-kit/api/models/background/Background';
import {Border} from '@mintdata/mint-kit/api/models/Border';
import {BoxShadow} from '@mintdata/mint-kit/api/models/BoxShadow';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {ABooleanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/ABooleanConfig';
import {AStringEnumConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AEnumConfig';
import {AStringConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AStringConfig';
import {BeanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BeanConfig';
import * as React from 'react';
import {BindingConfig, BindingIconPlace, BindingTooltipPlace} from '@mintdata/mint-kit/api/properties/BindingConfig';

export const DEFAULT_TILES_NUMBER_OF_COUNT:number = 2;

export class DnDModel
{
    @Property<ABoolean>({
        title: 'Is D&D Enabled',
        tip: 'Is D&D Enabled',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean,
        binding: BindingConfig.with(null, BindingIconPlace.RightMiddle, BindingTooltipPlace.Right)
    })
    public isEnabled:ABoolean;

    @Property<AString>({
        title: 'Group Name',
        tip: 'Group Name',
        isRequired: true,
        typeConfig: AStringConfig,
        typeCtor: AString
    })
    public groupName:AString = null;

    @Property<ABoolean>({
        title: 'Is Draggable',
        tip: 'Is Draggable',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean
    })
    public isDraggable:ABoolean = null;

    @Property<ABoolean>({
        title: 'Is Drop Target',
        tip: 'Is Drop Target',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean
    })
    public isDropTarget:ABoolean = null;

    @Property<DnDDropTargetHighlightType>({
        title: 'Drop Target Highlight Type',
        tip: 'Drop Target Highlight Type',
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: DnDDropTargetHighlightType
    })
    public dropTargetHighlightType:DnDDropTargetHighlightType = null;

    @Property<DnDHoveredDropTargetHighlightType>({
        title: 'Hovered Drop Target Highlight Type',
        tip: 'Hovered Drop Target Highlight Type',
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: DnDHoveredDropTargetHighlightType
    })
    public hoveredDropTargetHighlightType:DnDHoveredDropTargetHighlightType = null;

    @Property<DnDDividerPlacementType>({
        title: 'Divider Placement',
        tip: 'Divider Placement',
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: DnDDividerPlacementType
    })
    public dividerPlacementType:DnDDividerPlacementType = null;

    @Property<ABoolean>({
        title: 'Custom Preview Style',
        tip: 'Custom Preview Style',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean
    })
    public isCustomPreviewStyle:ABoolean;

    @Property<Background>({
        title: 'Background',
        tip: 'Background',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Background
    })
    previewBackground:Background;

    @Property<Border>({
        title: 'Border',
        tip: 'Border',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: Border
    })
    previewBorder:Border;

    @Property<BoxShadow>({
        title: 'Shadows',
        tip: 'Shadows',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: BoxShadow
    })
    previewBoxShadow:BoxShadow;

    @Property<Background>({
        title: 'Target Background',
        tip: 'Target Background',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Background
    })
    targetBackground:Background;

    @Property<Border>({
        title: 'Target Border',
        tip: 'Target Border',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: Border
    })
    targetBorder:Border;

    @Property<Background>({
        title: 'Hovered Target Background',
        tip: 'Hovered Target Background',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Background
    })
    hoveredTargetBackground:Background;

    @Property<Border>({
        title: 'Hovered Target Border',
        tip: 'Hovered Target Border',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: Border
    })
    hoveredTargetBorder:Border;

    public constructor(isEnabled:ABoolean = ABoolean.false(),
                       groupName:AString = AString.fromString('DnD Default Group'),
                       isDraggable:ABoolean = ABoolean.false(),
                       isDropTarget:ABoolean = ABoolean.false(),
                       dropTargetHighlightType:DnDDropTargetHighlightType = DnDDropTargetHighlightType.none(),
                       hoveredDropTargetHighlightType:DnDHoveredDropTargetHighlightType = DnDHoveredDropTargetHighlightType.none(),
                       dividerPlacementType:DnDDividerPlacementType = DnDDividerPlacementType.topBottomEdge(),
                       isCustomPreviewStyle:ABoolean = ABoolean.false(),
                       previewBackground:Background = Background.default(),
                       previewBorder:Border = Border.default(),
                       previewBoxShadow:BoxShadow = BoxShadow.default(),
                       targetBackground:Background = Background.default(),
                       targetBorder:Border = Border.default(),
                       hoveredTargetBackground:Background = Background.default(),
                       hoveredTargetBorder:Border = Border.default())
    {
        this.isEnabled = isEnabled;
        this.groupName = groupName;
        this.isDraggable = isDraggable;
        this.isDropTarget = isDropTarget;
        this.dropTargetHighlightType = dropTargetHighlightType;
        this.hoveredDropTargetHighlightType = hoveredDropTargetHighlightType;
        this.dividerPlacementType = dividerPlacementType;
        this.isCustomPreviewStyle = isCustomPreviewStyle;
        this.previewBackground = previewBackground;
        this.previewBorder = previewBorder;
        this.previewBoxShadow = previewBoxShadow;
        this.targetBackground = targetBackground;
        this.targetBorder = targetBorder;
        this.hoveredTargetBackground = hoveredTargetBackground;
        this.hoveredTargetBorder = hoveredTargetBorder;
    }
}
